// shared-lib/group-customization/mergeLinks.js
//
// Combine the existing `groupCustomization.links` of a fixture group with a
// freshly-emitted set of auto-links (e.g. from `emitVanityLinks`).
//
// Semantics (see claude-docs/2026-06-07-vanity-auto-links-and-generation.md,
// Phase 1c + Open Q1/Q2):
//
//   - Auto-links are fully regenerated on every build pass. Any existing link
//     with `source: 'auto'` is dropped and replaced by the emitted set, so a
//     relationship that no longer holds (e.g. an overhead light that was
//     removed) doesn't linger as a stale link. A user-deleted auto-link
//     therefore comes back on the next rebuild — Q2 recommendation (b).
//
//   - User-drawn links (`source !== 'auto'`) survive a rebuild. A user link
//     whose tuple matches an emitted auto-link wins — the emitted duplicate is
//     dropped so the editor doesn't show two handles for the same constraint.
//
//   - When the caller passes `validIds` (the live object id set after a
//     rebuild), links that reference a no-longer-existing object are pruned.
//     Re-emission produces fresh link ids each pass, so dedup is by the
//     *tuple of referenced endpoints*, never by id.
//
// Type names are compared through `canonicalType`, so a stored `rail-x` and
// a freshly emitted `center-on` over the same members are ONE constraint.

const { canonicalType } = require('./linkSemantics');
const { refKey } = require('./refs');

// Object ids a link constrains — the FREE endpoints only. Fixed refs (a wall,
// an opening) are not children, so they take no part in liveness pruning or
// the editor's "which children does this touch" queries.
function linkRefs(link) {
  switch (canonicalType(link?.type)) {
    case 'rp':
      return [link.sourceId, link.targetId];
    case 'copy':
    case 'mirror':
      return [link.parentAId, link.parentBId];
    case 'frame':
      return [link.objectAId, link.objectBId];
    case 'rail-y':
    case 'center-on':
      return Array.isArray(link.memberIds) ? [...link.memberIds] : [];
    case 'on-wall':
      return link.subjectId ? [link.subjectId] : [];
    case 'align-end':
      return []; // a run end and a geometry / run-end target: no free objects
    default:
      return [];
  }
}

// Every endpoint key a link names, fixed refs included. Used by `linkKey` so
// "sink centered on the window" and "sink centered on the faucet" stay
// distinct constraints even when their free members coincide.
function endpointKeys(link) {
  const keys = linkRefs(link).map((id) => refKey(id));
  switch (canonicalType(link?.type)) {
    case 'center-on':
      for (const f of link.fixedMembers || []) keys.push(refKey(f));
      break;
    case 'frame':
      if (link.axis) keys.push(refKey(link.axis));
      break;
    case 'on-wall':
      if (link.wall) keys.push(refKey(link.wall));
      break;
    case 'align-end':
      if (link.runEnd) keys.push(refKey(link.runEnd));
      if (link.target) keys.push(refKey(link.target));
      break;
    default:
      break;
  }
  return keys;
}

// Canonical dedup key. `rp` is directional so its ids stay ordered; the
// symmetric types sort their endpoint keys so the same set in either order
// collapses to one key. The canonical type is part of the key, so the two
// rails over one member set stay distinct constraints.
function linkKey(link) {
  const t = canonicalType(link?.type);
  switch (t) {
    case 'rp':
      return `rp|${link.sourceId}|${link.targetId}`;
    case 'on-wall':
      return `on-wall|${link.subjectId}|${link.wall ? refKey(link.wall) : ''}`;
    case 'copy':
    case 'mirror':
    case 'frame':
    case 'rail-y':
    case 'center-on':
    case 'align-end': {
      const keys = endpointKeys(link)
        .map((k) => String(k))
        .sort();
      return `${t}|${keys.join('|')}`;
    }
    default:
      return `${link?.type}|${link?.id}`;
  }
}

/**
 * Merge existing links with a freshly-emitted auto-link set.
 *
 * @param {Object[]} [existing] — current `groupCustomization.links`.
 * @param {Object[]} [emitted]  — auto-links from this build pass (each
 *   already stamped `source: 'auto'`).
 * @param {Object}   [opts]
 * @param {Set<string>} [opts.validIds] — live object ids after the rebuild.
 *   When provided, any link referencing an absent id is dropped (both user
 *   and auto). Omit to skip liveness pruning.
 * @returns {Object[]} merged link list: surviving user links first, then the
 *   emitted auto-links that no user link already owns.
 */
function mergeLinks(existing = [], emitted = [], opts = {}) {
  const validIds = opts.validIds || null;
  const isLive = (link) =>
    validIds == null || linkRefs(link).every((id) => validIds.has(id));

  const userLinks = (existing || []).filter(
    (l) => l && l.source !== 'auto' && isLive(l)
  );
  const userKeys = new Set(userLinks.map(linkKey));

  const seen = new Set(userKeys);
  const autoLinks = [];
  for (const l of emitted || []) {
    if (!l) continue;
    const key = linkKey(l);
    if (seen.has(key)) continue; // user link owns it, or already emitted
    if (!isLive(l)) continue;
    seen.add(key);
    autoLinks.push(l);
  }

  return [...userLinks, ...autoLinks];
}

module.exports = {
  mergeLinks,
  // Exposed for tests; callers should use mergeLinks.
  linkRefs,
  linkKey,
  endpointKeys,
};
